import { Button } from '@repo/ui/button'
import { CommandShortcut } from '@repo/ui/command'
import { Options, useT } from '@repo/ui/hooks'
import { Toaster as ToasterSonner } from '@repo/ui/sonner'
import { Toaster } from '@repo/ui/toaster'
import { cn } from '@repo/ui/utils'
import { Check, Loader2, TriangleAlert } from 'lucide-react'
import { useEffect, useRef } from 'react'
import Markdown from 'react-markdown'
import { toast } from 'sonner'

import { AppDispatch } from '@/main'
import { adminStoreActions, storeActions } from '@/store/actions'
import { Resource } from '@/store/admin'
import { Document } from '@/store/document'
import { useStoreDispatch, useStoreState } from '@/store/hooks'
import { Notification } from '@/store/notifications/notification'
import { Task } from '@/store/task'

const cleanString = (str: string | undefined) => str?.replace('notifications.', '')

type NotificationContent = {
  title: string | undefined
  description: string | undefined
}

const buildNotificationContent = (
  notification: Notification,
  t: (key: string, options?: Options) => string,
): NotificationContent => {
  let titlePrefix = ''
  let title: string | undefined = undefined
  let description: string | undefined = undefined

  if ('titlePrefix' in notification) {
    titlePrefix = notification.titlePrefix + ': '
  }

  if ('titleTransKey' in notification) {
    title = notification.titleTransKey ? titlePrefix + t(notification.titleTransKey) : undefined
  } else if ('title' in notification) {
    title = titlePrefix + notification.title
  }

  if ('descriptionTransKey' in notification) {
    description = notification.descriptionTransKey
      ? t(notification.descriptionTransKey, notification.descriptionTransOptions)
      : undefined
  } else if ('description' in notification) {
    description = notification.description
  }

  return { title, description }
}

const renderDescription = (description: string | undefined) => (
  <Markdown
    components={{
      p({ children, ...props }) {
        return (
          <p className="text-primary dark:text-primary-300 text-sm leading-6" {...props}>
            {children}
          </p>
        )
      },
      code({ className, children, ...props }) {
        return (
          <code
            className={cn(
              'bg-primary-50 dark:bg-primary-800 text-primary rounded-sm p-1 font-sans italic tracking-normal dark:text-white',
              className,
            )}
            {...props}
          >
            {children}
          </code>
        )
      },
    }}
  >
    {cleanString(description)}
  </Markdown>
)

const getNotificationIcon = (iconType: string | undefined) => {
  switch (iconType) {
    case 'check':
      return <Check className="size-5 animate-pulse text-green-500" />
    case 'loader':
      return <Loader2 className="text-primary-400 size-5 animate-spin" />
    case 'warn':
      return <TriangleAlert className="size-5 text-red-500" />
    default:
      return undefined
  }
}

const handleUndoAction = (
  undo: NonNullable<Notification['undo']>,
  dispatch: AppDispatch,
  notificationId: string,
  toastId: string | number,
) => {
  const { actionType, object, type } = undo

  dispatch(storeActions.undoFromNotification({ notificationId }))
  toast.dismiss(toastId)

  if (actionType === 'delete') {
    if (type === 'task') {
      void dispatch(storeActions.task.restore({ task: object as Task, notify: false }))
    } else if (type === 'resource') {
      void dispatch(
        adminStoreActions.resource.restore({
          resourceToRestore: object as Resource,
          notify: false,
        }),
      )
    } else if (type === 'document') {
      void dispatch(
        storeActions.beneficiary.document.restore({
          documentId: (object as Document).id,
          notify: false,
        }),
      )
    }
  } else if (actionType === 'archive' && type === 'task') {
    void dispatch(storeActions.task.unarchive({ task: object as Task, notify: false }))
  } else if (actionType === 'unarchive' && type === 'task') {
    void dispatch(storeActions.task.archive({ task: object as Task, notify: false }))
  }
}

const setupUndoKeyboardShortcut = (onUndo: () => void) => {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.metaKey && e.key === 'z') {
      onUndo()
    }
  }

  window.addEventListener('keydown', handleKeyDown)

  return () => {
    window.removeEventListener('keydown', handleKeyDown)
  }
}

const createToast = (
  notification: Notification,
  content: NotificationContent,
  dispatch: AppDispatch,
  tc: (key: string) => string,
) => {
  const toastId = toast(cleanString(content.title), {
    description: renderDescription(content.description),
    dismissible: true,
    cancel: true,
    closeButton: true,
    icon: getNotificationIcon(notification.icon),
  })

  if (!notification.undo) {
    return null
  }

  const cleanupUndo = () => {
    cleanupKeyboardListener()
  }

  const performUndo = () => {
    cleanupUndo()
    if (notification.undo) handleUndoAction(notification.undo, dispatch, notification.id, toastId)
  }

  const cleanupKeyboardListener = setupUndoKeyboardShortcut(performUndo)

  toast(cleanString(content.title), {
    id: toastId,
    action: (
      <div className="flex flex-1 justify-end">
        <Button onClick={performUndo} variant="outline">
          {tc('undo')} <CommandShortcut className="ml-2">⌘Z</CommandShortcut>
        </Button>
      </div>
    ),
    onDismiss: cleanupUndo,
    onAutoClose: cleanupUndo,
  })

  return { cleanUpEventListener: cleanupUndo }
}

export function Notifications() {
  const dispatch = useStoreDispatch()
  const { t, tc } = useT({ keyPrefix: 'notifications' })
  const notifications = useStoreState(({ notification }) => notification.all)
  const processedNotifications = useRef<Set<string>>(new Set())

  useEffect(() => {
    const activeNotifications = notifications.filter((n) => !n.undo?.isUndone)

    const cleanupHandlers = activeNotifications
      .map((notification) => {
        if (processedNotifications.current.has(notification.id)) {
          return null
        }

        processedNotifications.current.add(notification.id)
        const content = buildNotificationContent(notification, t)
        return createToast(notification, content, dispatch, tc)
      })
      .filter((handler): handler is { cleanUpEventListener: () => void } => handler !== null)

    return () => {
      cleanupHandlers.forEach((handler) => handler.cleanUpEventListener())
    }
  }, [notifications, dispatch, t, tc])

  return (
    <div>
      <Toaster />
      <ToasterSonner />
    </div>
  )
}
